import type {
  Announcements,
  ScreenReaderInstructions,
  UniqueIdentifier,
} from "@dnd-kit/core";
import { useAnnounce } from "@src/components/live-announcer";
import { useCallback, useMemo, useRef } from "react";

import { useReorderLabels } from "./use-reorder-labels";

interface ReorderAnnouncementsOptions {
  ids: string[];
  getName: (id: string) => string;
}

export function useReorderAnnouncements({
  ids,
  getName,
}: ReorderAnnouncementsOptions) {
  const labels = useReorderLabels();
  const announce = useAnnounce();
  const lastOverId = useRef<UniqueIdentifier | null>(null);

  const describe = useCallback(
    (id: UniqueIdentifier) => {
      const key = String(id);
      return {
        name: getName(key),
        position: ids.indexOf(key) + 1,
        total: ids.length,
      };
    },
    [getName, ids],
  );

  const announcements = useMemo<Announcements>(
    () => ({
      onDragStart: ({ active }) => {
        lastOverId.current = active.id;
        const { name, position, total } = describe(active.id);
        return labels.pickedUp(name, position, total);
      },
      onDragOver: ({ active, over }) => {
        if (!over || over.id === lastOverId.current) return undefined;
        lastOverId.current = over.id;
        const { position, total } = describe(over.id);
        return labels.moved(getName(String(active.id)), position, total);
      },
      onDragEnd: ({ active, over }) => {
        lastOverId.current = null;
        const { position, total } = describe(over?.id ?? active.id);
        return labels.dropped(getName(String(active.id)), position, total);
      },
      onDragCancel: ({ active }) => {
        lastOverId.current = null;
        const { name, position, total } = describe(active.id);
        return labels.cancelled(name, position, total);
      },
    }),
    [describe, getName, labels],
  );

  const screenReaderInstructions = useMemo<ScreenReaderInstructions>(
    () => ({ draggable: labels.instructions }),
    [labels],
  );

  const announceMove = useCallback(
    (movedText: string, saving: boolean) =>
      announce(
        [movedText, saving ? labels.saving : ""].filter(Boolean).join(" "),
      ),
    [announce, labels],
  );
  const announceSaved = useCallback(
    () => announce(labels.saved),
    [announce, labels],
  );
  const announceSaveFailed = useCallback(
    (name: string, position: number, total: number) =>
      announce(labels.saveFailed(name, position, total), "assertive"),
    [announce, labels],
  );

  return {
    announcements,
    screenReaderInstructions,
    labels,
    announceMove,
    announceSaved,
    announceSaveFailed,
  };
}
